import * as React from 'react'
import { Loader2 } from 'lucide-react'
import {
  type EntityResolution,
  type FieldResolution,
  type MergeConflicts,
  type MergeResolutions,
  type TaggedValue
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import {
  BranchConflictView,
  conflictSpec,
  type ConflictSide,
  type ConflictSpec
} from './BranchConflictView'
import { conflictSplit } from './conflict-view-model'

export interface MergeConflictResolverProps {
  conflicts: MergeConflicts
  /** The merge target — the "ours" side. */
  targetName: string
  /** The merge source — the "theirs" side. */
  sourceName: string
  submitLabel?: string
  submitting: boolean
  /**
   * The resolution map, lifted to the owner so the agent's streamed picks and
   * the user's manual clicks write the same state.
   */
  choices: Record<string, EntityResolution>
  onChoicesChange: React.Dispatch<React.SetStateAction<Record<string, EntityResolution>>>
  /** Per-conflict agent rationale, rendered as a note where present. */
  rationales?: Record<string, string>
  onCancel: () => void
  onSubmit: (resolutions: MergeResolutions) => void
}

/**
 * The conflict resolver: every entity both branches touched, with the three
 * versions (base / keep-target / take-source). Operators aren't git users, so
 * the sides read "Keep <target>" / "Take <source>" — ours/theirs stays an
 * API-level term. A conflict resolves either whole-entity (the column header)
 * or per property (each conflicting field picks its own side); a structural
 * conflict — one side removed the entity — is whole-entity only. Nothing merges
 * until every conflict is resolved.
 */
export function MergeConflictResolver({
  conflicts,
  targetName,
  sourceName,
  submitLabel = 'Merge with resolutions',
  submitting,
  choices,
  onChoicesChange: setChoices,
  rationales,
  onCancel,
  onSubmit
}: MergeConflictResolverProps) {
  // Per-conflict resolution shape (whole-entity-only + which fields differ),
  // used to gate completeness and to seed a whole-entity pick into fields.
  // The server's authoritative field set rides each conflict, so gating here
  // always matches what the merge assembler will demand.
  const specs = React.useMemo(() => {
    const m = new Map<string, ConflictSpec>()
    for (const c of conflicts.nodes) m.set(c.id, conflictSpec(c.ours, c.theirs, c.fields))
    for (const c of conflicts.rels) m.set(c.id, conflictSpec(c.ours, c.theirs, c.fields))
    return m
  }, [conflicts])

  // A conflict is resolved when it has a whole-entity side, a split, or — for a
  // per-field resolution — every conflicting field has a side or a content value.
  const isResolved = React.useCallback(
    (id: string): boolean => {
      const choice = choices[id]
      if (choice === undefined) return false
      if (typeof choice === 'string') return true
      if (conflictSplit(choice)) return true
      const spec = specs.get(id)
      if (!spec || spec.wholeEntityOnly) return false
      return spec.fields.every((f) => fieldResolved((choice as Record<string, FieldResolution>)[f]))
    },
    [choices, specs]
  )

  // The resolved ids back the counter and the conflict view's unresolved count
  // + "unresolved only" filter.
  const resolvedIds = React.useMemo(() => {
    const ids = new Set<string>()
    for (const c of conflicts.nodes) if (isResolved(c.id)) ids.add(c.id)
    for (const c of conflicts.rels) if (isResolved(c.id)) ids.add(c.id)
    for (const c of conflicts.postgres ?? []) {
      if (choices[c.id] === 'ours' || choices[c.id] === 'theirs') ids.add(c.id)
    }
    return ids
  }, [conflicts, isResolved, choices])

  const total = conflicts.nodes.length + conflicts.rels.length + (conflicts.postgres?.length ?? 0)
  const resolved = resolvedIds.size
  const allResolved = resolved === total

  const chooseAll = (side: ConflictSide) => {
    const all: Record<string, EntityResolution> = {}
    for (const c of conflicts.nodes) all[c.id] = side
    for (const c of conflicts.rels) all[c.id] = side
    for (const c of conflicts.postgres ?? []) all[c.id] = side
    setChoices(all)
  }

  const chooseSide = (id: string, side: ConflictSide) =>
    setChoices((prev) => ({ ...prev, [id]: side }))

  // The per-field map to override, seeded so a prior whole-entity pick carries
  // every field before this one changes. A prior split is abandoned (start fresh).
  const seedFields = (
    id: string,
    cur: EntityResolution | undefined
  ): Record<string, FieldResolution> => {
    if (cur && typeof cur === 'object' && !conflictSplit(cur)) {
      return { ...(cur as Record<string, FieldResolution>) }
    }
    if (cur === 'ours' || cur === 'theirs') {
      return Object.fromEntries(
        (specs.get(id)?.fields ?? []).map((f) => [f, cur] as [string, FieldResolution])
      )
    }
    return {}
  }

  const chooseField = (id: string, field: string, side: ConflictSide) =>
    setChoices((prev) => {
      const fields = seedFields(id, prev[id])
      fields[field] = side
      return { ...prev, [id]: fields }
    })

  const chooseFieldValue = (id: string, field: string, value: TaggedValue) =>
    setChoices((prev) => {
      const fields = seedFields(id, prev[id])
      fields[field] = { value }
      return { ...prev, [id]: fields }
    })

  // Resolve an add/add node collision by splitting it into two nodes.
  const chooseSplit = (id: string, keep: ConflictSide, newId: string) =>
    setChoices((prev) => ({ ...prev, [id]: { split: { keep, newId } } }))

  const handleSubmit = () => {
    if (!allResolved || submitting) return
    const resolutions: MergeResolutions = { nodes: {}, rels: {} }
    if (conflicts.postgres?.length) resolutions.postgres = {}
    for (const c of conflicts.nodes) resolutions.nodes![c.id] = choices[c.id]
    for (const c of conflicts.rels) resolutions.rels![c.id] = choices[c.id]
    for (const c of conflicts.postgres ?? []) {
      const choice = choices[c.id]
      if (choice === 'ours' || choice === 'theirs') resolutions.postgres![c.id] = choice
    }
    onSubmit(resolutions)
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="xs" disabled={submitting} onClick={() => chooseAll('ours')}>
          Keep {targetName} for all
        </Button>
        <Button
          variant="outline"
          size="xs"
          disabled={submitting}
          onClick={() => chooseAll('theirs')}
        >
          Take {sourceName} for all
        </Button>
        <Button
          variant="ghost"
          size="xs"
          className="ml-auto text-st-down hover:bg-st-down-bg hover:text-st-down"
          disabled={submitting || Object.keys(choices).length === 0}
          onClick={() => setChoices({})}
        >
          Clear resolutions
        </Button>
      </div>

      <BranchConflictView
        conflicts={conflicts}
        targetName={targetName}
        sourceName={sourceName}
        choices={choices}
        rationales={rationales}
        disabled={submitting}
        resolvedIds={resolvedIds}
        onChooseSide={chooseSide}
        onChooseField={chooseField}
        onChooseFieldValue={chooseFieldValue}
        onChooseSplit={chooseSplit}
      />

      {(conflicts.postgres ?? []).map((conflict) => (
        <div key={conflict.id} className="rounded-md border border-line p-3">
          <div className="text-xs font-semibold text-ink">{conflict.table} row</div>
          <div className="mt-1 break-all font-mono text-11 text-ink-3">
            {JSON.stringify(conflict.key)}
          </div>
          <div className="mt-1 text-11 text-ink-3">
            {conflict.fields?.length
              ? `Both branches changed: ${conflict.fields.join(', ')}`
              : 'Both branches changed this row'}
          </div>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant={choices[conflict.id] === 'ours' ? 'default' : 'outline'}
              size="xs"
              disabled={submitting}
              onClick={() => chooseSide(conflict.id, 'ours')}
            >
              Keep {targetName}
            </Button>
            <Button
              type="button"
              variant={choices[conflict.id] === 'theirs' ? 'default' : 'outline'}
              size="xs"
              disabled={submitting}
              onClick={() => chooseSide(conflict.id, 'theirs')}
            >
              Take {sourceName}
            </Button>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between gap-2 border-t border-line pt-2">
        <span className="text-xs text-ink-3">
          {resolved} of {total} resolved
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" disabled={!allResolved || submitting} onClick={handleSubmit}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

// A per-field resolution counts as resolved when it picks a side or supplies a
// corrected content value.
function fieldResolved(fr: FieldResolution | undefined): boolean {
  if (fr === 'ours' || fr === 'theirs') return true
  return typeof fr === 'object' && fr !== null && 'value' in fr
}
